import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const HowIsFHTreated = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'FH Treatment Options: A Complete Overview',
      duration: '11:20',
      views: '22K views',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/eeb89fbdb9d9f4ffce338807acd2439a.jpg',
    },
    {
      title: 'Statins and Beyond: Managing Genetic Cholesterol',
      duration: '13:45',
      views: '19K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Close%20up%20of%20modern%20cholesterol%20lowering%20medications%20statins%20ezetimibe%20pills%20arranged%20on%20clean%20white%20surface%20with%20stethoscope%20and%20heart%20model%20in%20background%20warm%20natural%20lighting%20professional%20pharmaceutical%20healthcare%20photography&width=400&height=250&seq=fh-treat-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Lifestyle Changes That Support FH Treatment',
      duration: '9:55',
      views: '27K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Healthy%20heart%20diet%20Mediterranean%20food%20spread%20with%20salmon%20avocado%20nuts%20olive%20oil%20vegetables%20on%20wooden%20table%20alongside%20exercise%20equipment%20and%20blood%20pressure%20monitor%20warm%20natural%20lighting%20lifestyle%20healthcare%20photography&width=400&height=250&seq=fh-treat-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Familial Heart Disease Treatment
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              How Is Familial<br />Hypercholesterolemia Treated?
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Familial Hypercholesterolemia (FH) is a lifelong genetic condition, but with the right combination of medications, lifestyle changes, and regular monitoring, patients can dramatically lower their LDL cholesterol and reduce the risk of premature heart disease.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Doctor%20writing%20prescription%20for%20familial%20hypercholesterolemia%20treatment%20plan%20with%20statin%20medication%20bottles%20and%20heart%20health%20charts%20on%20desk%20in%20warm%20modern%20cardiology%20clinic%20natural%20lighting%20professional%20medical%20photography%20clean%20organized%20workspace&width=400&height=300&seq=fh-treat-blog-card-001&orientation=landscape"
                  alt="Blog articles about FH treatment"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth guide to FH treatment options</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Medical%20specialist%20presenting%20familial%20hypercholesterolemia%20treatment%20video%20lecture%20showing%20cholesterol%20lowering%20therapy%20comparison%20chart%20on%20large%20screen%20in%20modern%20medical%20conference%20room%20warm%20professional%20lighting%20healthcare%20education%20setting&width=400&height=300&seq=fh-treat-video-card-001&orientation=landscape"
                  alt="Educational videos about FH treatment"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Visual explainers on FH therapies</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Detailed%20medical%20infographic%20poster%20about%20familial%20hypercholesterolemia%20FH%20treatment%20pathway%20showing%20medication%20types%20lifestyle%20changes%20and%20monitoring%20schedule%20modern%20healthcare%20data%20visualization%20coral%20teal%20and%20cream%20color%20palette%20professional%20medical%20illustration%20clean%20design&width=400&height=300&seq=fh-treat-infograph-card-001&orientation=landscape"
                  alt="FH treatment infographics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Visual guide to FH treatment pathways</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Beyond the Diet: 5 Critical Truths About Living with Familial Hypercholesterolemia (FH)
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Jan 18, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  12 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Medical%20professional%20explaining%20familial%20hypercholesterolemia%20treatment%20plan%20to%20patient%20with%20genetic%20heart%20condition%20using%20heart%20model%20and%20medication%20chart%20in%20modern%20cardiology%20clinic%20warm%20professional%20healthcare%20photography%20clean%20aesthetic&width=800&height=400&seq=fhtx-blog-hero-living&orientation=landscape"
                  alt="Living with Familial Hypercholesterolemia"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Section 1: The Invisible Head Start */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Invisible Head Start
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For most people, a diagnosis of high cholesterol is a middle-age milestone—a "check engine" light that flickers on after decades of sedentary habits or processed diets. But for those living with Familial Hypercholesterolemia (FH), the narrative is entirely different. Whether it is the heterozygous form (FeFH) or the more severe homozygous form (FoFH), FH is a genetic condition that places the body on an accelerated timeline. While the general population begins worrying about their heart health in their 40s or 50s, individuals with FH have had a "cholesterol clock" ticking since the day they were born.
                </p>
              </div>

              {/* Section 2: It's a "From-Birth" Condition */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's a "From-Birth" Condition, Not a Middle-Age Problem
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The physiological reality of FH is a struggle against internal biology, not just external choices. In a typical body, the liver efficiently clears Low-Density Lipoprotein (LDL)—the "bad" cholesterol—from the bloodstream. In a body with FH, a genetic mutation breaks this filtration system.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The result is a silent, cumulative buildup. This excess LDL sticks to artery walls from infancy, forming plaques that narrow the vessels and restrict vital blood flow. Because this exposure begins at birth, the cardiovascular damage that typically takes sixty years to develop can manifest in a patient's twenties or thirties. This is why clinical experts emphasize that we cannot wait for the symptoms of heart disease to appear before acting.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    "In FH, LDL is extremely high from birth, so early and aggressive reduction is vital."
                  </p>
                </div>
              </div>

              {/* Section 3: The Surprising Age for First-Line Treatment */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Surprising Age for First-Line Treatment
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Because the damage from FH is cumulative, the most effective window for intervention is during childhood. Waiting until adulthood to start treatment is often a reactive strategy rather than a preventive one. Current clinical guidelines suggest a proactive approach that often surprises parents: first-line treatment for children with FH should ideally begin between the ages of 8 and 10.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While the idea of putting a third-grader on a statin may feel counter-intuitive, it is a strategy backed by robust safety data. Medications like Atorvastatin and Rosuvastatin have been proven safe for pediatric use, serving as a critical "brake" on the genetic process before permanent arterial damage occurs.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Injected every 2–4 weeks</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Additional 50–60% LDL reduction</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Well-tolerated by most patients</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700">Proven cardiovascular benefit</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4: When Pills Aren't Enough */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    When Pills Aren't Enough: The High-Tech Treatment Frontier
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For those with FH, the "statin ceiling" is a common clinical hurdle. While standard statins work by blocking cholesterol production in the liver and can lower LDL by 30–50%, the genetic drive to produce cholesterol in FH patients is often too powerful for a single drug to overcome.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This is why "combination therapy" has become the clinical gold standard. Because the body is failing to manage cholesterol at multiple points—production, absorption, and clearance—doctors must use a multi-pronged pharmaceutical attack. When a single "silver bullet" pill fails to reach the aggressive targets required for safety, advanced therapies are introduced:
                </p>
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <h4 className="font-semibold text-gray-900 mb-2">Ezetimibe</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      This medication prevents cholesterol from being absorbed in the intestines, providing an additional 15–25% reduction.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#0d9488]">
                    <h4 className="font-semibold text-gray-900 mb-2">PCSK9 Inhibitors (Evolocumab/Alirocumab)</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      These advanced injectables help the liver remove more LDL from the blood, acting as "powerhouses" that can lower levels by up to 60%.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <h4 className="font-semibold text-gray-900 mb-2">Bempedoic Acid (Nexletol)</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      A newer liver-targeted option that reduces LDL by 15–20% and is often preferred by those who experience muscle sensitivity with traditional statins.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#0d9488]">
                    <h4 className="font-semibold text-gray-900 mb-2">Specialized Orphan Drugs</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      For the most severe FoFH cases, medications like Lomitapide or Evinacumab are deployed under specialist supervision to bypass traditional metabolic pathways.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 5: Lipoprotein Apheresis */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Lipoprotein Apheresis: "Dialysis" for Your Heart
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In the most severe cases of FH—particularly in homozygous cases or very high-risk heterozygous patients—even the most advanced drug cocktails may fall short. For these individuals, the solution is not chemical, but physical.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lipoprotein Apheresis is a sophisticated blood filtration process that functions much like dialysis for the heart. The patient's blood is passed through a machine that physically siphons out the LDL cholesterol before returning the cleansed blood to the body. The sheer intensity of this treatment—typically requiring several hours at a specialized center every 1–2 weeks—underscores the gravity of managing genetic cholesterol. It is a literal life-line for those whose DNA leaves them no other choice.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
                  <div className="flex items-center gap-3 mb-3">
                    <i className="ri-heart-pulse-line text-2xl text-[#f04e58]"></i>
                    <p className="text-gray-900 font-semibold">Treatment Details:</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700 text-sm">Several hours per session at specialized center</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700 text-sm">Required every 1–2 weeks</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700 text-sm">Physical removal of LDL cholesterol from blood</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#0d9488]"></div>
                      <span className="text-gray-700 text-sm">Life-saving option for severe FH cases</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 6: Lifestyle Strategy */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    6
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The "Genetic Support" Strategy: Why Lifestyle Still Matters
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The genetic nature of FH often leads to a dangerous fatalism—the idea that if the deck is stacked by DNA, the hand isn't worth playing. However, medical science suggests the opposite: because the baseline risk is higher, the "value" of every healthy choice is effectively doubled. While you cannot "out-run" a genetic mutation with a salad, healthy habits are the essential allies that allow medications to work at their peak efficiency.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-4 text-lg">The Lifestyle Framework for FH:</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f04e58]/10 flex-shrink-0 mt-1">
                        <i className="ri-restaurant-line text-[#f04e58]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Dietary Focus</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Prioritize a "heart-clean" intake of fruits, vegetables, whole grains, and lean proteins. It is vital to avoid triggers like butter, cheese, red meats, and fried foods.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0d9488]/10 flex-shrink-0 mt-1">
                        <i className="ri-drop-line text-[#0d9488]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Strategic Fats</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Shift toward olive or canola oils; strictly eliminate trans fats and minimize saturated fats.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f04e58]/10 flex-shrink-0 mt-1">
                        <i className="ri-run-line text-[#f04e58]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Physical Activity</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Commit to 30–45 minutes of daily exercise (walking, swimming, or cycling) to strengthen the cardiovascular system against existing plaque.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0d9488]/10 flex-shrink-0 mt-1">
                        <i className="ri-forbid-line text-[#0d9488]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">The Non-Negotiable</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Tobacco use must be avoided entirely, as smoking acts as an accelerant for plaque buildup in already vulnerable arteries.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f04e58]/10 flex-shrink-0 mt-1">
                        <i className="ri-line-chart-line text-[#f04e58]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Rigorous Monitoring</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Managing FH requires a data-driven approach. Expect a lipid panel every 3–6 months whenever treatment is being adjusted to ensure targets are hit.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Conclusion: The Power of the 50% Rule */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#f04e58]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Conclusion: The Power of the 50% Rule
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Living with FH is a lifelong commitment to aggressive risk management. Success is not measured by reaching a "normal" number, but by achieving a transformative reduction in cumulative exposure.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white mb-4">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    "People with FH need to reduce LDL cholesterol aggressively and early — ideally by 50% or more from baseline."
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By merging cutting-edge pharmacology with a disciplined lifestyle, even those with the most stubborn genetic profiles can rewrite their cardiovascular future.
                </p>
                <p className="text-gray-700 leading-relaxed font-medium text-lg">
                  If high cholesterol is a "silent" genetic thief, how early would you want to know the clock is ticking?
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Get Expert FH Treatment Guidance</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  Living with FH requires specialized care and a personalized treatment plan. Our cardiovascular specialists can help you navigate the latest treatment options and achieve your cholesterol goals.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Consultation
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How Is FH Treated? A Visual Explanation
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this educational video to understand the full range of treatment options available for Familial Hypercholesterolemia, from first-line medications to advanced therapies and lifestyle strategies.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-gray-900 aspect-video mb-8">
              <video
                controls
                className="w-full h-full"
                poster="https://readdy.ai/api/search-image?query=Medical%20education%20video%20thumbnail%20showing%20doctor%20explaining%20familial%20hypercholesterolemia%20treatment%20options%20with%20medication%20bottles%20statin%20pills%20and%20heart%20model%20on%20desk%20cinematic%20dark%20background%20warm%20coral%20and%20teal%20accent%20lighting%20professional%20healthcare%20content%20play%20button%20overlay&width=800&height=450&seq=fh-treat-video-poster-001&orientation=landscape"
              >
                <source
                  src="https://rr3---sn-nx57ynsd.googlevideo.com/videoplayback?expire=1771410584&ei=eHiVae3_KZK8sfIPs_zQwQI&ip=151.186.183.87&id=43abd7f7be471c72&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&met=1771403384,&mh=Vp&mm=32&mn=sn-nx57ynsd&ms=su&mv=u&mvi=3&pl=26&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=339.754&lmt=1771399322000491&mt=1771402096&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRAIgc0s4WjeZlNpe4xwiQc1WLy4ztUOr67nATN-RSX-h4owCIGvmfu3dKUG65EYpUOiLgycB7El33aWmEHt1a5g5jHLx&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRQIgMZt0ghVB1I4zS8qEeNR0SBk0ENu3dF0PyyKVE-TrspoCIQChbFVnHi1tDCNFhSlE5uK0BS84YeBYthVp8XAnO-_wHg=="
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">FH Treatment Series</h3>
                  <p className="text-sm text-gray-500">Educational Content</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This series covers the complete treatment landscape for Familial Hypercholesterolemia—from statins and ezetimibe as first-line therapies, to PCSK9 inhibitors and advanced options like lipoprotein apheresis. Learn how lifestyle changes support medication, and why family screening is a critical part of the treatment journey.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                FH Treatment Pathway: A Visual Guide
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A comprehensive visual overview of how Familial Hypercholesterolemia is treated—from first-line medications and advanced therapies to lifestyle changes and family screening.
              </p>
            </div>

            {/* Main Infographic */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/e2219c3cdfc434cff31785452ac5980d.png"
                  alt="Managing FH: Your Roadmap to Lowering LDL - comprehensive infographic showing LDL targets, treatment options, and lifestyle changes"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-4 gap-4 mb-12">
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#f04e58] mb-2">30–50%</div>
                <p className="text-xs text-gray-600">LDL reduction with statins</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#0d9488] mb-2">15–25%</div>
                <p className="text-xs text-gray-600">Additional reduction with ezetimibe</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#f04e58] mb-2">50–60%</div>
                <p className="text-xs text-gray-600">Further reduction with PCSK9i</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#0d9488] mb-2">Normal</div>
                <p className="text-xs text-gray-600">Life expectancy with treatment</p>
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-capsule-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Medication Therapy</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A combination of statins, ezetimibe, and PCSK9 inhibitors forms the backbone of FH treatment, achieving up to 85% LDL reduction.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-heart-pulse-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Lifestyle Support</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Heart-healthy diet, regular exercise, no smoking, and weight management are essential foundations that support every medication.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-group-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Family Screening</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Cascade screening of all first-degree relatives is critical—one diagnosis can protect an entire family through early detection.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HowIsFHTreated;
